import * as Sentry from '@sentry/react';
import { z } from 'zod';

export const workspaceFilterSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('production') }),
  z.object({ kind: z.literal('workspace'), id: z.string().min(1) })
]);

export type WorkspaceFilterSelection = z.infer<
  typeof workspaceFilterSelectionSchema
>;

export const DEFAULT_WORKSPACE_FILTER: WorkspaceFilterSelection = {
  kind: 'production'
};

// The workspace `version` field is opaque JSON on the GraphQL side; parse defensively.
// Mirrors the studio-ui WorkspaceVersion shape used by the version badge.
const workspaceVersionSchema = z.object({
  type: z.string().min(1),
  displayName: z.string().min(1),
  name: z.string().min(1).optional()
});

export type WorkspaceVersionInfo = z.infer<typeof workspaceVersionSchema>;

export const parseWorkspaceVersion = (
  version: unknown
): WorkspaceVersionInfo | null => {
  // No version is the normal "latest" case — don't treat it as an error.
  if (version === null || version === undefined) {
    return null;
  }
  const parsed = workspaceVersionSchema.safeParse(version);
  if (!parsed.success) {
    // A present-but-unparseable version means the backend contract drifted;
    // report it (a missing version is silent, handled above).
    Sentry.captureException(parsed.error, {
      tags: { fn: 'parseWorkspaceVersion' }
    });
    return null;
  }
  return parsed.data;
};

// Workflow `settings` is opaque JSON; parse defensively to read the subscriptions flag.
const workflowSettingsSchema = z.object({
  disableSubscriptions: z.boolean().optional()
});

export const isSubscriptionsDisabled = (settings: unknown): boolean => {
  const parsed = workflowSettingsSchema.safeParse(settings);
  return parsed.success && parsed.data.disableSubscriptions === true;
};
